import { Tag } from "antd";
import React, { useContext, useEffect, useState } from "react";
import { Container, Image, Nav, Navbar, NavDropdown } from "react-bootstrap";
import { Link, NavLink, useNavigate } from "react-router-dom";
import AdminModalContext from "../context/AdminModalContext";
import AuthContext from "../context/AuthContext";
import WorkingDaysUnsetAlert from "./WorkingDaysUnsetAlert";

// Issue #84: NavLink の className は (isActive) を受け取る関数を渡せる。
// 現在地のリンクへ Bootstrap の active クラスを付けて、どのページを開いているか
// ヘッダー上で分かるようにする。
const navLinkClass = ({ isActive }) =>
	isActive ? "nav-link active" : "nav-link";

// ヘッダーに出す今日の日付を計算し直す間隔。画面を開いたまま日付をまたいでも、
// 最長この間隔で翌日の表示へ切り替わる。日付の変わり目ちょうどに合わせた
// タイマーは PC のスリープ中に止まり、復帰後も古い日付のまま残るため、
// 短い間隔で計算し直す方式にしている（同じ文字列なら再描画は起きない）。
const TODAY_REFRESH_INTERVAL_MS = 60 * 1000;

const formatTodayLabel = () =>
	`Today: ${new Date().toLocaleDateString("en-US", {
		year: "numeric",
		month: "2-digit",
		day: "2-digit",
	})}`;

const useTodayLabel = () => {
	const [label, setLabel] = useState(formatTodayLabel);

	useEffect(() => {
		const timerId = setInterval(
			() => setLabel(formatTodayLabel()),
			TODAY_REFRESH_INTERVAL_MS,
		);
		return () => clearInterval(timerId);
	}, []);

	return label;
};

const Header = () => {
	const { user, logoutUser } = useContext(AuthContext);
	const { requestAdminModal } = useContext(AdminModalContext);
	const navigate = useNavigate();

	// Issue #84: Admin page メニューの表示条件（isAdmin または is_superuser）と
	// バッジの表示条件を同じ式に揃える。以前はバッジだけ isAdmin 単独判定で、
	// is_superuser のみのユーザーには「Admin page が出るのにバッジが無い」
	// 非対称が生じていた。権限判定そのものは変更しない。
	const hasAdminAccess = Boolean(user?.isAdmin || user?.is_superuser);

	const todayLabel = useTodayLabel();

	const openAdminModal = (modalName) => {
		requestAdminModal(modalName);
		navigate("/dashboard");
	};

	return (
		// スクロールしてもナビゲーションが常に見えるよう上部に固定する。
		// Bootstrap の sticky-top は z-index 1020 で antd の Modal（1000）より
		// 手前に出てしまうため、Modal の背景より奥・表の固定列より手前に下げる
		<Navbar
			collapseOnSelect
			expand="md"
			sticky="top"
			className="bg-body-tertiary"
			style={{ zIndex: 100 }}
		>
			{/* navbar-expand-md は md 以上で折り返しを禁止するため、稼働日数の警告を
			    ナビゲーションの下の行へ回せるよう折り返しを許可する。lg 未満では
			    固定幅（720px）だとブランドとメニューが 1 行に収まらず、固定表示の
			    ヘッダーが 3 行に伸びるため、画面幅いっぱいに広げる */}
			<Container fluid="lg" className="flex-wrap">
				{/* Issue #84: href="/" の素の a タグはクリックでフルページリロードが
				    走り、SPA の状態が破棄される。Link へ差し替えてクライアント遷移にする */}
				<Navbar.Brand
					as={Link}
					to="/"
					style={{ display: "flex", alignItems: "center" }}
				>
					<Image
						src={process.env.PUBLIC_URL + "/images/logo.png"}
						alt="Logo"
						fluid
						style={{ marginRight: "10px", width: "50px", height: "auto" }}
					/>
					{/* 今日の日付はシステム名の下に小さく添える。横に並べるとブランドの
					    幅が伸び、中間幅でメニューが次の行へ押し出される */}
					<span style={{ display: "flex", flexDirection: "column" }}>
						<span>NOCE Timesheet System</span>
						<small
							style={{
								fontSize: 12,
								fontWeight: "bold",
								color: "#6c757d",
								lineHeight: 1.2,
							}}
							data-testid="header-today"
						>
							{todayLabel}
						</small>
					</span>
				</Navbar.Brand>
				{/* Navbar.Collapse 内に置くと md 幅未満でハンバーガーメニューを開くまで
				    見えなくなるため、常時表示領域に配置する（表示はナビゲーションの行の下に 1 行で並ぶ） */}
				<WorkingDaysUnsetAlert />
				<Navbar.Toggle aria-controls="responsive-navbar-nav" />
				<Navbar.Collapse id="responsive-navbar-nav">
					<Nav className="ms-auto">
						{user && (
							// end を付けないと "/" は全てのパスへ前方一致してしまい、
							// どのページでも常時 active になる
							<NavLink to="/" end className={navLinkClass}>
								Hello, {user.username} 👋{" "}
								{hasAdminAccess ? <Tag color="green">Admin</Tag> : null}
							</NavLink>
						)}
						{user ? (
							<>
								{/* Issue #89 (PR #111 Codexレビュー対応): /dashboard はAdminRouteで
								    管理者限定になったため、一般ユーザーには押しても即ホームへ
								    戻るだけのリンクを見せない */}
								{hasAdminAccess && (
									<NavLink to="/dashboard" className={navLinkClass}>
										Dashboard
									</NavLink>
								)}
								<NavLink to="/profile" className={navLinkClass}>
									My Profile
								</NavLink>
								{hasAdminAccess && (
									<NavDropdown title="Admin page" id="admin-page-dropdown">
										<NavDropdown.Item
											onClick={() => openAdminModal("workingDays")}
										>
											Working Days Master
										</NavDropdown.Item>
										{/* Issue #154: 部門マスタの管理（一覧・新規作成・改名・削除）。
										    稼働日数マスタと同じ管理者権限（isAdmin または is_superuser）で開く */}
										<NavDropdown.Item
											onClick={() => openAdminModal("departments")}
										>
											Department Management
										</NavDropdown.Item>
										{user.isAdmin && (
											<NavDropdown.Item onClick={() => openAdminModal("excel")}>
												Upload Excel
											</NavDropdown.Item>
										)}
									</NavDropdown>
								)}
								{/* Issue #84: 遷移先を持たない操作なので Link（href なしの a）ではなく
								    button にする。キーボード操作・支援技術から「押せるもの」として
								    正しく扱われる */}
								<Nav.Link as="button" type="button" onClick={logoutUser}>
									Logout
								</Nav.Link>
							</>
						) : (
							<>
								<NavLink to="/login" className={navLinkClass}>
									Login
								</NavLink>
								{/* <Link to="/register" className="nav-link">
                Register
              </Link> */}
							</>
						)}
					</Nav>
				</Navbar.Collapse>
			</Container>
		</Navbar>
	);
};

export default Header;
